import { z } from 'zod';
import { cursorPageSchema, pageQuerySchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, } from '../common/primitives.js';
import { appliedRateSchema, currencyCodeSchema, missingRateSchema } from '../finance/finance.js';
import { parseFixed } from '../finance/fixed-decimal.js';
import { citedDecisionSchema, founderRefSchema } from '../funding/funding.js';
import { policyVersionStatusSchema } from '../governance/policies.js';
/**
 * Internal company valuation (spec §7.18, §18 "valuation data is incomplete", §19, ADR-0010).
 * Deterministic backend code computes every figure from stored inputs; an AI model may only
 * explain them. The result is a low/base/high range for founder planning — never a formal
 * investment, accounting, tax or legal valuation.
 */
export const VALUATION_DISCLAIMER_VERSION = '2026-10';
export const VALUATION_DISCLAIMER = 'Internal analytical estimate for founder planning only. It is not a formal investment, fair-market, accounting, tax or legal valuation, and must not be presented as one.';
/** A manual input dated this many days before the valuation date or earlier is stale. */
export const VALUATION_STALE_AFTER_DAYS = 90;
/** A comparable multiple set older than this at the valuation date lowers confidence. */
export const COMPARABLE_STALE_AFTER_DAYS = 365;
/** Default spread of the growth scenarios around the observed growth, in percentage points. */
export const DEFAULT_GROWTH_SPREAD_POINTS = '20';
// --- enums ----------------------------------------------------------------------------------------
export const METRIC_UNITS = ['MONEY', 'PERCENT', 'COUNT', 'RATIO', 'MONTHS'];
export const metricUnitSchema = z.enum(METRIC_UNITS);
/** MISSING ⇔ no value. STALE: dated before the freshness limit. ESTIMATED: entered as an estimate. */
export const METRIC_QUALITIES = ['OBSERVED', 'MANUAL', 'ESTIMATED', 'STALE', 'MISSING'];
export const metricQualitySchema = z.enum(METRIC_QUALITIES);
export const VALUATION_METHODS = ['REVENUE_MULTIPLE', 'ARR_MULTIPLE', 'EBITDA_MULTIPLE'];
export const valuationMethodSchema = z.enum(VALUATION_METHODS);
export const VALUATION_CONFIDENCES = ['LOW', 'MEDIUM', 'HIGH'];
export const valuationConfidenceSchema = z.enum(VALUATION_CONFIDENCES);
/** Where a metric comes from: the journal, founder records, a person, or a formula. */
export const METRIC_ORIGINS = ['BOOKS', 'FOUNDER_RECORDS', 'MANUAL', 'DERIVED'];
export const metricOriginSchema = z.enum(METRIC_ORIGINS);
export const METRIC_GROUPS = [
    'REVENUE',
    'CUSTOMERS',
    'MARGINS',
    'COSTS_CASH',
    'BALANCE_SHEET',
    'CONTRACTS',
    'UNIT_ECONOMICS',
    'PRODUCT',
];
export const metricGroupSchema = z.enum(METRIC_GROUPS);
/**
 * Every metric the valuation collects. BOOKS/FOUNDER_RECORDS values are read from the system;
 * MANUAL ones are entered with a source and an as-of date; DERIVED ones are formulas of the others.
 */
export const METRIC_CATALOG = [
    {
        key: 'revenue_ttm',
        label: 'Revenue, last 12 months',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'BOOKS',
        description: 'Operating income posted in the 12 complete months before the valuation date.',
    },
    {
        key: 'revenue_prior_ttm',
        label: 'Revenue, the 12 months before',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'BOOKS',
        description: 'Operating income in months 13–24 before the valuation date (for yearly growth).',
    },
    {
        key: 'revenue_last_month',
        label: 'Revenue, last complete month',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'BOOKS',
        description: 'Operating income of the last complete month.',
    },
    {
        key: 'revenue_previous_month',
        label: 'Revenue, the month before',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'BOOKS',
        description: 'Operating income of the month before the last complete month.',
    },
    {
        key: 'revenue_history_months',
        label: 'Months with recorded revenue',
        unit: 'COUNT',
        group: 'REVENUE',
        origin: 'BOOKS',
        description: 'Months with operating income among the last 24 complete months.',
    },
    {
        key: 'growth_mom',
        label: 'Revenue growth, month over month',
        unit: 'PERCENT',
        group: 'REVENUE',
        origin: 'DERIVED',
        description: 'Last complete month against the month before.',
    },
    {
        key: 'growth_yoy',
        label: 'Revenue growth, year over year',
        unit: 'PERCENT',
        group: 'REVENUE',
        origin: 'DERIVED',
        description: 'Last 12 months against the 12 months before.',
    },
    {
        key: 'mrr',
        label: 'Monthly recurring revenue (MRR)',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'MANUAL',
        description: 'Recurring revenue contracted for one month at the valuation date.',
        plannedSource: 'Billing system',
    },
    {
        key: 'arr',
        label: 'Annual recurring revenue (ARR)',
        unit: 'MONEY',
        group: 'REVENUE',
        origin: 'DERIVED',
        description: 'MRR × 12.',
    },
    {
        key: 'recurring_revenue_share',
        label: 'Recurring share of revenue',
        unit: 'PERCENT',
        group: 'REVENUE',
        origin: 'MANUAL',
        description: 'Share of last-12-month revenue that is recurring (the rest is services/one-time).',
        plannedSource: 'Billing system',
    },
    {
        key: 'customers',
        label: 'Paying customers',
        unit: 'COUNT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Paying customers at the valuation date.',
        plannedSource: 'Billing system or CRM',
    },
    {
        key: 'customers_new_ttm',
        label: 'New customers, last 12 months',
        unit: 'COUNT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Customers won in the last 12 months.',
        plannedSource: 'CRM',
    },
    {
        key: 'customers_lost_ttm',
        label: 'Lost customers, last 12 months',
        unit: 'COUNT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Customers that stopped paying in the last 12 months.',
        plannedSource: 'Billing system',
    },
    {
        key: 'churn_monthly',
        label: 'Monthly customer churn',
        unit: 'PERCENT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Share of customers lost per month, when measurable.',
        plannedSource: 'Billing system',
    },
    {
        key: 'arpa_monthly',
        label: 'Average revenue per customer, monthly (ARPA)',
        unit: 'MONEY',
        group: 'CUSTOMERS',
        origin: 'DERIVED',
        description: 'MRR ÷ paying customers.',
    },
    {
        key: 'top_customer_share',
        label: 'Largest customer share of revenue',
        unit: 'PERCENT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Share of last-12-month revenue from the single largest customer.',
    },
    {
        key: 'top5_customer_share',
        label: 'Top-5 customers share of revenue',
        unit: 'PERCENT',
        group: 'CUSTOMERS',
        origin: 'MANUAL',
        description: 'Share of last-12-month revenue from the five largest customers.',
    },
    {
        key: 'gross_margin',
        label: 'Gross margin',
        unit: 'PERCENT',
        group: 'MARGINS',
        origin: 'MANUAL',
        description: 'Revenue minus direct costs, as a share of revenue. The books have no cost-of-revenue accounts yet.',
        signed: true,
    },
    {
        key: 'contribution_margin',
        label: 'Contribution margin',
        unit: 'PERCENT',
        group: 'MARGINS',
        origin: 'MANUAL',
        description: 'Only when costs can be allocated to customers or products.',
        signed: true,
    },
    {
        key: 'operating_expenses_ttm',
        label: 'Operating expenses, last 12 months',
        unit: 'MONEY',
        group: 'COSTS_CASH',
        origin: 'BOOKS',
        description: 'Operating expenses posted in the 12 complete months before the valuation date.',
    },
    {
        key: 'operating_result_ttm',
        label: 'Operating result, last 12 months',
        unit: 'MONEY',
        group: 'COSTS_CASH',
        origin: 'DERIVED',
        description: 'Revenue minus operating expenses; used as the EBITDA proxy (the books have no separate depreciation or amortisation accounts).',
    },
    {
        key: 'net_burn_monthly',
        label: 'Net burn per month',
        unit: 'MONEY',
        group: 'COSTS_CASH',
        origin: 'BOOKS',
        description: 'Average monthly operating expenses minus operating income over the finance burn window; negative when the company earns more than it spends.',
    },
    {
        key: 'cash',
        label: 'Cash',
        unit: 'MONEY',
        group: 'COSTS_CASH',
        origin: 'BOOKS',
        description: 'Book balance of the company bank and cash accounts at the valuation date; restricted cash is left out.',
    },
    {
        key: 'runway_months',
        label: 'Runway',
        unit: 'MONTHS',
        group: 'COSTS_CASH',
        origin: 'DERIVED',
        description: 'Cash ÷ net burn, rounded down; not available when the company is not burning cash.',
    },
    {
        key: 'receivables',
        label: 'Accounts receivable',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'BOOKS',
        description: 'Balance of accounts-receivable accounts (context; part of working capital).',
    },
    {
        key: 'payables',
        label: 'Accounts and tax payable',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'BOOKS',
        description: 'Balance of accounts-payable and tax-payable accounts (context; part of working capital).',
    },
    {
        key: 'financial_debt',
        label: 'Financial debt',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'MANUAL',
        description: 'Bank loans and other interest-bearing debt outstanding. Enter 0 if there is none.',
        plannedSource: 'Accounting system',
    },
    {
        key: 'founder_loans',
        label: 'Owed to founders',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'FOUNDER_RECORDS',
        description: 'What the company owes founders: advances, loans, reimbursements and credits.',
    },
    {
        key: 'founder_receivables',
        label: 'Owed by founders',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'FOUNDER_RECORDS',
        description: 'Unpaid funding-call shares and founder debts to the company (shown for context; not added to value).',
    },
    {
        key: 'distributions_payable',
        label: 'Declared distributions not yet paid',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'BOOKS',
        description: 'Balance of the distributions-payable account.',
    },
    {
        key: 'other_debt_like',
        label: 'Other debt-like items',
        unit: 'MONEY',
        group: 'BALANCE_SHEET',
        origin: 'MANUAL',
        description: 'Other material liabilities to deduct (e.g. deferred payments, legal claims). Enter 0 if there are none.',
    },
    {
        key: 'backlog',
        label: 'Contracted backlog',
        unit: 'MONEY',
        group: 'CONTRACTS',
        origin: 'MANUAL',
        description: 'Signed contract value not yet recognised as revenue.',
        plannedSource: 'Billing system',
    },
    {
        key: 'pipeline',
        label: 'Sales pipeline (unsigned)',
        unit: 'MONEY',
        group: 'CONTRACTS',
        origin: 'MANUAL',
        description: 'Open opportunities; never counted as revenue or backlog.',
        plannedSource: 'CRM',
    },
    {
        key: 'pipeline_win_rate',
        label: 'Pipeline win rate',
        unit: 'PERCENT',
        group: 'CONTRACTS',
        origin: 'MANUAL',
        description: 'Historical share of pipeline value that closes.',
        plannedSource: 'CRM',
    },
    {
        key: 'risk_adjusted_pipeline',
        label: 'Risk-adjusted pipeline',
        unit: 'MONEY',
        group: 'CONTRACTS',
        origin: 'DERIVED',
        description: 'Pipeline × win rate (context only; never in the valuation methods).',
    },
    {
        key: 'cac',
        label: 'Customer acquisition cost (CAC)',
        unit: 'MONEY',
        group: 'UNIT_ECONOMICS',
        origin: 'MANUAL',
        description: 'Sales and marketing cost per new customer, only when attribution is reliable.',
    },
    {
        key: 'ltv',
        label: 'Customer lifetime value (LTV)',
        unit: 'MONEY',
        group: 'UNIT_ECONOMICS',
        origin: 'DERIVED',
        description: 'ARPA × gross margin ÷ monthly churn.',
    },
    {
        key: 'ltv_to_cac',
        label: 'LTV to CAC',
        unit: 'RATIO',
        group: 'UNIT_ECONOMICS',
        origin: 'DERIVED',
        description: 'LTV ÷ CAC.',
    },
    {
        key: 'cac_payback_months',
        label: 'CAC payback',
        unit: 'MONTHS',
        group: 'UNIT_ECONOMICS',
        origin: 'DERIVED',
        description: 'CAC ÷ (ARPA × gross margin), in months.',
    },
    {
        key: 'active_users',
        label: 'Active users',
        unit: 'COUNT',
        group: 'PRODUCT',
        origin: 'MANUAL',
        description: 'Monthly active users or accounts, if tracked.',
    },
];
export const METRIC_KEYS = METRIC_CATALOG.map((metric) => metric.key);
export const metricKeySchema = z.enum(METRIC_KEYS);
const BY_KEY = new Map(METRIC_CATALOG.map((m) => [m.key, m]));
export function metricDefinition(key) {
    const definition = BY_KEY.get(key);
    if (definition === undefined)
        throw new RangeError(`Unknown metric ${key}`);
    return definition;
}
export const MANUAL_METRIC_KEYS = METRIC_CATALOG.filter((m) => m.origin === 'MANUAL').map((m) => m.key);
export const BOOK_METRIC_KEYS = METRIC_CATALOG.filter((m) => m.origin === 'BOOKS' || m.origin === 'FOUNDER_RECORDS').map((m) => m.key);
/** The input each method multiplies. */
export const METHOD_METRIC = {
    REVENUE_MULTIPLE: 'revenue_ttm',
    ARR_MULTIPLE: 'arr',
    EBITDA_MULTIPLE: 'operating_result_ttm',
};
/** Debt-like items deducted from enterprise value (cash is added). */
export const BRIDGE_DEDUCTIONS = [
    'financial_debt',
    'founder_loans',
    'distributions_payable',
    'other_debt_like',
];
// --- values ---------------------------------------------------------------------------------------
/** Metric input: up to 16 integer digits and 4 decimals; manual inputs only. */
export const metricValueSchema = z
    .string()
    .trim()
    .regex(/^-?\d{1,16}(\.\d{1,4})?$/, 'Enter a number such as 1250.50, with at most 4 decimals');
/** Valuation multiple, e.g. `3.5` (0 < m ≤ 999, at most 4 decimals). */
export const multipleValueSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Enter a multiple such as 3 or 4.25')
    .refine((value) => /[1-9]/.test(value), { message: 'A multiple must be above zero' });
/** Units at scale 4 of an already format-validated decimal string. */
function units4(value) {
    return parseFixed(value, 4);
}
/** Percentage 0–100 with at most 2 decimals. */
export const assumptionPercentSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter a percentage such as 25 or 12.5')
    .refine((value) => units4(value) <= units4('100'), {
    message: 'The percentage cannot exceed 100',
});
// --- comparable multiple sets ---------------------------------------------------------------------
export const comparableSetSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    method: valuationMethodSchema,
    lowMultiple: z.string(),
    baseMultiple: z.string(),
    highMultiple: z.string(),
    /** Date the multiples describe. */
    asOf: isoDateSchema,
    geography: z.string(),
    businessModel: z.string(),
    sourceName: z.string(),
    sourceReference: z.string(),
    status: policyVersionStatusSchema,
    approvalDecision: citedDecisionSchema.nullable(),
    approvalReference: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
});
export const comparableSetListSchema = z.object({
    items: z.array(comparableSetSchema),
    permissions: z.object({ canPropose: z.boolean(), canApprove: z.boolean() }),
});
export const proposeComparableSetRequestSchema = z
    .object({
    name: z.string().trim().min(3).max(150),
    method: valuationMethodSchema,
    lowMultiple: multipleValueSchema,
    baseMultiple: multipleValueSchema,
    highMultiple: multipleValueSchema,
    asOf: isoDateSchema,
    geography: z.string().trim().min(2).max(100),
    businessModel: z.string().trim().min(2).max(150),
    sourceName: z.string().trim().min(2).max(150),
    /** Link or citation of the published data (report, database extract, transaction list). */
    sourceReference: z.string().trim().min(3).max(1000),
})
    .superRefine((value, ctx) => {
    const low = units4(value.lowMultiple);
    const base = units4(value.baseMultiple);
    const high = units4(value.highMultiple);
    if (low > base) {
        ctx.addIssue({ code: 'custom', message: 'Low cannot exceed base', path: ['lowMultiple'] });
    }
    if (base > high) {
        ctx.addIssue({ code: 'custom', message: 'Base cannot exceed high', path: ['highMultiple'] });
    }
});
export const approveComparableSetRequestSchema = z
    .object({
    approvalDecisionId: uuidSchema.nullable().default(null),
    approvalReference: z.string().trim().max(500).nullable().default(null),
})
    .refine((value) => value.approvalDecisionId !== null || (value.approvalReference ?? '').trim().length >= 3, {
    message: 'Cite the finalized decision or the approval reference (e.g. signed minutes)',
    path: ['approvalReference'],
});
export const closeComparableSetRequestSchema = z.object({ reason: reasonSchema });
// --- requests -------------------------------------------------------------------------------------
export const manualMetricInputSchema = z.object({
    key: metricKeySchema,
    value: metricValueSchema,
    /** Date the figure describes; older than {@link VALUATION_STALE_AFTER_DAYS} days → stale. */
    asOf: isoDateSchema,
    /** Where the figure comes from, e.g. "Billing export 2026-10-01". */
    source: z.string().trim().min(3).max(300),
    /** Entered as an estimate rather than read from a record. */
    estimated: z.boolean().default(false),
});
/** Weight per method in percent; omitted methods get 0. Null = equal weights. */
export const methodWeightsSchema = z
    .object({
    REVENUE_MULTIPLE: assumptionPercentSchema.optional(),
    ARR_MULTIPLE: assumptionPercentSchema.optional(),
    EBITDA_MULTIPLE: assumptionPercentSchema.optional(),
})
    .refine((value) => Object.values(value).some((weight) => units4(weight) > 0n), {
    message: 'Give at least one method a weight above zero',
});
export const valuationAssumptionsSchema = z.object({
    methodWeights: methodWeightsSchema.nullable().default(null),
    /** Discount applied to equity value (e.g. for illiquidity), percent; null = none. */
    discountPercent: assumptionPercentSchema
        .refine((value) => units4(value) <= units4('90'), {
        message: 'A discount above 90 % is not meaningful',
    })
        .nullable()
        .default(null),
    /** Spread of the growth scenarios, percentage points; null = the default. */
    growthSpreadPoints: assumptionPercentSchema.nullable().default(null),
});
function uniqueKeys(items) {
    return new Set(items.map((item) => item.key)).size === items.length;
}
const manualInputsSchema = z
    .array(manualMetricInputSchema)
    .max(METRIC_CATALOG.length)
    .refine(uniqueKeys, { message: 'Each metric can be entered once' })
    .refine((items) => items.every((item) => MANUAL_METRIC_KEYS.includes(item.key)), {
    message: 'Only manual metrics can be entered; figures from the books are read by the system',
});
const multipleSetIdsSchema = z
    .array(uuidSchema)
    .max(VALUATION_METHODS.length)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Choose each set once' });
export const createValuationRequestSchema = z
    .object({
    currency: currencyCodeSchema,
    manualInputs: manualInputsSchema,
    /** One approved comparable set per method to apply. */
    multipleSetIds: multipleSetIdsSchema,
    assumptions: valuationAssumptionsSchema.prefault({}),
    /** Required when any assumption differs from its default. */
    assumptionReason: reasonSchema.nullable().default(null),
})
    .superRefine((value, ctx) => {
    const a = value.assumptions;
    const overrides = a.methodWeights !== null || a.discountPercent !== null || a.growthSpreadPoints !== null;
    if (overrides && value.assumptionReason === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Explain why the assumptions differ from the defaults',
            path: ['assumptionReason'],
        });
    }
});
export const bookOverrideSchema = z.object({
    key: metricKeySchema,
    value: metricValueSchema,
    reason: reasonSchema,
});
/**
 * New snapshot from an existing one: the same book figures (not refreshed) with changed manual
 * inputs, overridden book figures and/or assumptions. The previous snapshot never changes.
 */
export const recalculateValuationRequestSchema = z.object({
    manualInputs: manualInputsSchema,
    bookOverrides: z
        .array(bookOverrideSchema)
        .max(BOOK_METRIC_KEYS.length)
        .refine(uniqueKeys, { message: 'Each figure can be overridden once' })
        .refine((items) => items.every((item) => BOOK_METRIC_KEYS.includes(item.key)), { message: 'Only figures read from the books or founder records can be overridden here' })
        .default([]),
    multipleSetIds: multipleSetIdsSchema,
    assumptions: valuationAssumptionsSchema.prefault({}),
    /** Why the valuation is recalculated (recorded on the new snapshot and in the audit log). */
    reason: reasonSchema,
});
export const valuationInputsQuerySchema = z.object({ currency: currencyCodeSchema.optional() });
export const valuationQuerySchema = pageQuerySchema;
// --- views ----------------------------------------------------------------------------------------
export const metricViewSchema = z.object({
    key: metricKeySchema,
    label: z.string(),
    unit: metricUnitSchema,
    group: metricGroupSchema,
    origin: metricOriginSchema,
    /** Decimal string; null when missing or not applicable (see `note`). */
    value: z.string().nullable(),
    currency: z.string().nullable(),
    quality: metricQualitySchema,
    /** What the figure is based on: accounts, period, records, or the source a person named. */
    sourceReference: z.string().nullable(),
    /** When the figure was read from the system, or the date a person said it describes. */
    sourceTimestamp: isoDateTimeSchema.nullable(),
    note: z.string().nullable(),
});
export const valuationWarningSchema = z.object({
    code: z.enum([
        'MISSING_INPUT',
        'STALE_INPUT',
        'ESTIMATED_INPUT',
        'MISSING_FX_RATE',
        'SHORT_HISTORY',
        'METHOD_EXCLUDED',
        'METHODS_DISAGREE',
        'STALE_COMPARABLES',
        'NEGATIVE_EQUITY',
        'LOW_RECURRING_SHARE',
        'CUSTOMER_CONCENTRATION',
        'BRIDGE_ITEM_MISSING',
        'DRAFT_TRANSACTIONS_EXCLUDED',
    ]),
    severity: z.enum(['WARNING', 'INFO']),
    message: z.string(),
    metricKey: metricKeySchema.nullable(),
});
export const rangeSchema = z.object({ low: z.string(), base: z.string(), high: z.string() });
export const valuationMethodResultSchema = z.object({
    method: valuationMethodSchema,
    status: z.enum(['APPLIED', 'EXCLUDED']),
    /** Why it is excluded, or caveats for an applied method. */
    explanation: z.string(),
    metricKey: metricKeySchema,
    metricValue: z.string().nullable(),
    multipleSet: z
        .object({
        id: uuidSchema,
        name: z.string(),
        asOf: isoDateSchema,
        sourceName: z.string(),
        geography: z.string(),
        businessModel: z.string(),
    })
        .nullable(),
    multiples: rangeSchema.nullable(),
    /** Enterprise value from this method alone (whole currency units). */
    enterpriseValue: rangeSchema.nullable(),
    /** Effective weight in the blend, percent (0 for excluded methods). */
    weight: z.string(),
});
export const bridgeItemSchema = z.object({
    key: metricKeySchema,
    label: z.string(),
    /** Signed effect on equity value: cash is added, debt-like items are subtracted. */
    effect: z.string().nullable(),
    status: z.enum(['INCLUDED', 'MISSING']),
});
export const assumptionViewSchema = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    defaultValue: z.string().nullable(),
    isOverride: z.boolean(),
    reason: z.string().nullable(),
});
export const confidenceFactorSchema = z.object({ label: z.string(), points: z.number().int() });
export const driverSchema = z.object({
    label: z.string(),
    /** Signed contribution to base equity value. */
    amount: z.string(),
    /** Share of all absolute contributions, percent with 1 decimal. */
    share: z.string(),
    explanation: z.string(),
});
export const sensitivitySchema = z.object({
    multiples: z.array(z.object({
        method: valuationMethodSchema,
        rows: z.array(z.object({
            label: z.enum(['LOW', 'BASE', 'HIGH']),
            multiple: z.string(),
            enterpriseValue: z.string(),
            equityValue: z.string(),
        })),
    })),
    growth: z.object({
        /** Why the growth scenarios could not be built, if so. */
        note: z.string().nullable(),
        baseGrowthPercent: z.string().nullable(),
        rows: z.array(z.object({
            growthPercent: z.string(),
            forwardRevenue: z.string(),
            equityValue: z.string(),
        })),
    }),
    adjustments: z.array(z.object({ label: z.string(), equityValue: z.string(), change: z.string() })),
});
export const founderIndicativeValueSchema = z.object({
    founder: founderRefSchema,
    percentage: z.string(),
    value: rangeSchema,
});
export const valuationNarrativeSchema = z.object({
    text: z.string(),
    /** SYSTEM: written by deterministic code. AI: generated by a model; may be incorrect. */
    source: z.enum(['SYSTEM', 'AI']),
    model: z.string().nullable(),
    /** Present when an AI attempt was made but rejected or failed (the system text is shown). */
    aiFallbackReason: z.string().nullable(),
});
export const valuationSnapshotSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    valuationDate: isoDateSchema,
    currency: z.string(),
    equityValue: rangeSchema,
    confidence: valuationConfidenceSchema,
    methodsApplied: z.array(valuationMethodSchema),
    previous: z.object({ id: uuidSchema, number: z.number().int() }).nullable(),
    recalculationReason: z.string().nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
});
export const valuationSnapshotSchema = valuationSnapshotSummarySchema.extend({
    inputPeriod: z.object({ from: isoDateSchema, to: isoDateSchema }),
    enterpriseValue: rangeSchema,
    methods: z.array(valuationMethodResultSchema),
    bridge: z.object({
        items: z.array(bridgeItemSchema),
        /** Equity value before the discount. */
        equityBeforeDiscount: rangeSchema,
        discountPercent: z.string(),
    }),
    inputs: z.array(metricViewSchema),
    ratesUsed: z.array(appliedRateSchema),
    missingRates: z.array(missingRateSchema),
    assumptions: z.array(assumptionViewSchema),
    warnings: z.array(valuationWarningSchema),
    confidenceScore: z.number().int(),
    confidenceRationale: z.string(),
    confidenceFactors: z.array(confidenceFactorSchema),
    drivers: z.array(driverSchema),
    sensitivity: sensitivitySchema,
    founderValues: z.object({
        capTable: z
            .object({ versionNumber: z.number().int(), effectiveFrom: isoDateSchema })
            .nullable(),
        /** Null with `note` when there is no positive equity value or no cap table. */
        items: z.array(founderIndicativeValueSchema).nullable(),
        note: z.string(),
    }),
    narrative: valuationNarrativeSchema,
    /** What was entered for this snapshot, to start a recalculation from. */
    settings: z.object({
        manualInputs: z.array(z.object({
            key: metricKeySchema,
            value: z.string(),
            asOf: isoDateSchema,
            source: z.string(),
            estimated: z.boolean(),
        })),
        bookOverrides: z.array(z.object({ key: metricKeySchema, value: z.string(), reason: z.string() })),
        multipleSetIds: z.array(uuidSchema),
        methodWeights: z.partialRecord(valuationMethodSchema, z.string()).nullable(),
        discountPercent: z.string().nullable(),
        growthSpreadPoints: z.string().nullable(),
    }),
    disclaimer: z.string(),
    disclaimerVersion: z.string(),
    calculationVersion: z.string(),
    later: z.array(z.object({ id: uuidSchema, number: z.number().int() })),
});
export const valuationSnapshotPageSchema = cursorPageSchema(valuationSnapshotSummarySchema);
export const valuationOverviewSchema = z.object({
    latest: valuationSnapshotSummarySchema.nullable(),
    currencies: z.array(z.string()),
    approvedSets: z.number().int(),
    proposedSets: z.number().int(),
    permissions: z.object({
        canValuate: z.boolean(),
        canManageComparables: z.boolean(),
    }),
    disclaimer: z.string(),
});
/** What a new valuation would start from: book figures now, the last manual inputs, approved sets. */
export const valuationInputsSchema = z.object({
    currency: z.string(),
    currencies: z.array(z.string()),
    valuationDate: isoDateSchema,
    inputPeriod: z.object({ from: isoDateSchema, to: isoDateSchema }),
    bookMetrics: z.array(metricViewSchema),
    ratesUsed: z.array(appliedRateSchema),
    missingRates: z.array(missingRateSchema),
    /** Manual inputs of the latest snapshot in this currency, to start from (may be stale). */
    lastManualInputs: z.array(z.object({
        key: metricKeySchema,
        value: z.string(),
        asOf: isoDateSchema,
        source: z.string(),
        estimated: z.boolean(),
        fromSnapshotNumber: z.number().int(),
    })),
    approvedSets: z.array(comparableSetSchema),
    defaults: z.object({ growthSpreadPoints: z.string(), staleAfterDays: z.number().int() }),
});
//# sourceMappingURL=valuation.js.map